import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  CollectionPreferences,
  Header,
  Pagination,
  Table,
  TableProps
} from '@amzn/awsui-components-react';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { NonCancelableEventHandler } from '@amzn/awsui-components-react/polaris/internal/events';

import { getString } from '../../common/ui-string-labels/ui-strings-utils';
import {
  buildColumnDefinitions,
  buildCollectionPreferencesProps,
  CellTypeRenderers,
  BuildColumnOptions
} from '../../common/utils/table-utils';
import { ColumnSetting } from '../../common/utils/config-models';
import EmptyTableBox from '../../common/EmptyTableBox/EmptyTableBox';
import { BackfillRun, ContentBackfillService } from '../../services/content-backfill-service';
import { usePreferences } from '../../hooks/usePreferences';
import { getRunStatusIndicatorType } from './backfill-status-config';
import runsTableConfig from './runs.table-config.json';

export interface BackfillRunsTableProps {
  onRunSelected: (run: BackfillRun | null) => void;
  onPageView?: () => void;
}

const PREFERENCES_KEY = 'BackfillRunsPreferences';
const columns: ColumnSetting[] = runsTableConfig.columns as unknown as ColumnSetting[];
const PREFS_CONFIG = buildCollectionPreferencesProps(columns);

/** Cell type renderers for runs table (status column). */
const RUNS_CELL_RENDERERS: CellTypeRenderers<BackfillRun> = {
  status_with_tooltip: {
    getIndicatorType: (status) => getRunStatusIndicatorType(status ?? '')
  }
};

/**
 * Processes selection change from the runs table.
 * Calls onRunSelected with the selected run (if exactly 1) or null (if 0 or >1).
 * Exported for direct unit testing since Cloudscape single-select radio does not
 * support UI-level deselection.
 */
export function handleRunSelectionChange(
  selectedItems: BackfillRun[],
  onRunSelected: (run: BackfillRun | null) => void
): void {
  if (selectedItems.length === 1) {
    onRunSelected(selectedItems[0]);
  } else {
    onRunSelected(null);
  }
}

export const BackfillRunsTable: FunctionComponent<BackfillRunsTableProps> = ({ onRunSelected, onPageView }) => {
  const [runs, setRuns] = useState<BackfillRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<BackfillRun[]>([]);

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ContentBackfillService.instance.getRuns();
      setRuns(response.runs);
    } catch (e: any) {
      setError(e.message || getString('contentBackfill.runsTable.errorHeader'));
    } finally {
      setLoading(false);
    }
  }, []);

  // Preferences (shared hook)
  const { currentPreferences, onPreferencesConfirmHandler } = usePreferences({
    preferencesKey: PREFERENCES_KEY,
    defaultPaging: PREFS_CONFIG.pageSizeOptions![0].value,
    columnsSettings: columns,
    refreshData: fetchRuns
  });

  useEffect(() => {
    fetchRuns();
    onPageView?.();
  }, [fetchRuns]);

  // Client-side pagination + sorting via useCollection (bounded list)
  const { items: paginatedRuns, collectionProps, paginationProps } = useCollection(runs, {
    pagination: { pageSize: currentPreferences.pageSize },
    sorting: {
      defaultState: { isDescending: true, sortingColumn: { sortingField: 'createdAt' } }
    }
  });

  const handleSelectionChange: NonCancelableEventHandler<TableProps.SelectionChangeDetail<BackfillRun>> = (e) => {
    setSelectedItems(e.detail.selectedItems);
    handleRunSelectionChange(e.detail.selectedItems, onRunSelected);
  };

  // Build column definitions from JSON config with extended cell types (single pass)
  const options: BuildColumnOptions<BackfillRun> = { cellRenderers: RUNS_CELL_RENDERERS };
  const currentColumnDefinitions = buildColumnDefinitions(
    columns,
    currentPreferences.visibleContent as readonly string[],
    options
  ) as TableProps.ColumnDefinition<BackfillRun>[];

  return (
    <>
      {error && (
        <Alert
          type="error"
          header={getString('contentBackfill.runsTable.errorHeader')}
          data-testid="backfill-runs-error-alert"
        >
          {error}
        </Alert>
      )}
      <Table<BackfillRun>
        {...collectionProps}
        data-testid="backfill-runs-table"
        loading={loading}
        loadingText={getString('contentBackfill.runsTable.loadingItems')}
        items={paginatedRuns}
        columnDefinitions={currentColumnDefinitions}
        trackBy="jobId"
        selectionType="single"
        selectedItems={selectedItems}
        onSelectionChange={handleSelectionChange}
        header={
          <Header
            variant="h3"
            description={getString('contentBackfill.runsTable.headerDescription')}
            counter={`(${runs.length})`}
          >
            {getString('contentBackfill.runsTable.headerTitle')}
          </Header>
        }
        pagination={<Pagination {...paginationProps} />}
        preferences={
          <CollectionPreferences
            title={getString('common.preferences.title')}
            confirmLabel={getString('common.preferences.confirmLabel')}
            cancelLabel={getString('common.preferences.cancelLabel')}
            pageSizePreference={{
              title: getString('common.preferences.pageSize.title'),
              options: PREFS_CONFIG.pageSizeOptions!
            }}
            stripedRowsPreference={{
              label: getString('common.preferences.stripedRows.label'),
              description: getString('common.preferences.stripedRows.description')
            }}
            visibleContentPreference={{
              title: getString('common.preferences.visibleContentPreference.title'),
              options: [
                {
                  label: getString('common.preferences.visibleContentPreference.columns'),
                  options: PREFS_CONFIG.visibleContentOptions
                }
              ]
            }}
            preferences={currentPreferences}
            onConfirm={onPreferencesConfirmHandler}
          />
        }
        empty={<EmptyTableBox />}
        variant="borderless"
        stripedRows={currentPreferences.stripedRows}
        resizableColumns
      />
    </>
  );
};
